/**
 * WL-K5a: localStorage helper for the pending CLIENT invite token (Kanzlei →
 * Mandant), carried across the auth flow (register/login → verify → dashboard →
 * accept panel).
 *
 * DELIBERATELY SEPARATE from the M11b advisor-invite token
 * ({@link ./advisorInviteToken}): a DIFFERENT localStorage key
 * (`pendingClientInviteToken`, NOT `pendingAdvisorInviteToken`). This is the
 * B-E5 requirement — `useIsAdvisorContext` reads `getPendingInviteToken()` (the
 * advisor key); if the client-invite token reused that key, a fresh mandant with
 * a pending client invite would be MISCLASSIFIED as an advisor and lose their
 * onboarding/magic-moment. Keeping the keys apart structurally prevents that.
 *
 * The token arrives in the mail link as `?clientInvite=<token>`.
 *
 * NOT flag-gated (unlike the advisor token util): the accepting mandant is not
 * yet Loop-B-Beta at capture time, so gating capture on beta would break the
 * flow. The token is just the value already delivered in the mandant's own
 * mail — storing it briefly adds no attack surface. The hard gate is server-side
 * (the invite's Kanzlei must be Loop-B-Beta).
 *
 * @module utils/clientInviteToken
 */

const PENDING_CLIENT_INVITE_TOKEN_KEY = 'pendingClientInviteToken';

/**
 * Returns the pending client-invite token from localStorage, or null if none
 * exists or localStorage is unavailable.
 */
export function getClientInviteToken(): string | null {
  try {
    return localStorage.getItem(PENDING_CLIENT_INVITE_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Stores the client-invite token so it survives the auth redirect chain. No-op
 * for a falsy token or when localStorage is unavailable.
 *
 * @param token - the plaintext token from the `?clientInvite=` URL parameter.
 */
export function setClientInviteToken(token: string): void {
  if (!token) return;
  try {
    localStorage.setItem(PENDING_CLIENT_INVITE_TOKEN_KEY, token);
  } catch {
    // Silently ignore (private-browsing quota / unavailable).
  }
}

/**
 * Removes the pending client-invite token. Must be called after a successful
 * accept (or explicit decline) so subsequent navigations do not re-trigger the
 * accept redirect.
 */
export function clearClientInviteToken(): void {
  try {
    localStorage.removeItem(PENDING_CLIENT_INVITE_TOKEN_KEY);
  } catch {
    // Silently ignore.
  }
}
